import { Link, useParams, useNavigate } from "react-router-dom";
import { ExportModal } from "@/components/ui/export-modal";
import {
  ArrowLeft, Mail, MousePointerClick, PlayCircle, CheckCircle2, CheckCircle,
  Clock, Send, XCircle, Download, Loader, Loader2, MessageSquare, Edit3, AlertCircle,
} from "lucide-react";
import { WaveIcon } from "@/components/common/WaveIcon";
import { useState, useEffect, useCallback } from "react";
import { engineApiClient, apiRequest } from "@/lib/apiClient";
import { engineEndpoints } from "@/lib/contracts";
import type { OutreachStateRecord, OutreachStatus } from "@/lib/contracts";

// ─── Brief parser ────────────────────────────────────────────────────────────

function parseOutreachBrief(brief: string): { subject: string; body: string } {
  let subject = '';
  let body = '';

  // Try to extract **SUBJECT LINE** block
  const subjectMatch = brief.match(/\*\*SUBJECT\s*LINE\*\*[\s\S]*?\n([^\n*]+)/i);
  if (subjectMatch) subject = subjectMatch[1].replace(/^-+\s*/, '').trim();

  // Try to extract **MESSAGE** block
  const messageMatch = brief.match(/\*\*MESSAGE\*\*[\s\S]*?\n([\s\S]*?)(?:\*\*[A-Z]|$)/i);
  if (messageMatch) body = messageMatch[1].trim();

  // Fallback: strip all ** markers and split on ---
  if (!subject || !body) {
    const clean = brief.replace(/\*\*[^*]+\*\*/g, '').replace(/^---+$/gm, '').trim();
    const lines = clean.split('\n').filter(l => l.trim());
    if (!subject && lines.length > 0) subject = lines[0].trim();
    if (!body) body = lines.slice(1).join('\n').trim();
  }

  // Ensure sign-off
  const signOff = 'Best,\nErica\nSearchline.';
  if (body && !body.includes('Best,')) {
    body = body.trimEnd() + '\n\n' + signOff;
  }

  return { subject, body };
}

// ─── Types ─────────────────────────────────────────────────────────────────

type QueueRowStatus = 'generating' | 'ready' | 'sending' | 'sent' | 'failed'

type QueueCandidate = {
  id: string
  name: string
  initials: string
  title: string
  company: string
  match: number
  scoreBand?: string
  emailSubject: string
  emailBody: string
  rowStatus: QueueRowStatus
}

// ─── Status config (sent section) ─────────────────────────────────────────

const STATUS_CONFIG: Record<OutreachStatus, {
  icon: React.ElementType
  color: string
  bg: string
  label: string
}> = {
  not_started:          { icon: Clock,             color: "text-gray-500",   bg: "bg-gray-500/10",   label: "Not Started" },
  prepared:             { icon: Mail,              color: "text-gray-400",   bg: "bg-gray-500/10",   label: "Prepared" },
  sent:                 { icon: Send,              color: "text-blue-400",   bg: "bg-blue-500/10",   label: "Email Sent" },
  email_opened:         { icon: Mail,              color: "text-sky-400",    bg: "bg-sky-500/10",    label: "Email Opened" },
  link_clicked:         { icon: MousePointerClick, color: "text-yellow-400", bg: "bg-yellow-500/10", label: "Link Clicked" },
  pre_screen_started:   { icon: PlayCircle,        color: "text-orange-400", bg: "bg-orange-500/10", label: "Pre-Screen Started" },
  pre_screen_completed: { icon: CheckCircle2,      color: "text-green-400",  bg: "bg-green-500/10",  label: "Pre-Screen Completed" },
  no_response:          { icon: Clock,             color: "text-red-400",    bg: "bg-red-500/10",    label: "No Response" },
  abandoned:            { icon: XCircle,           color: "text-red-400",    bg: "bg-red-500/10",    label: "Abandoned" },
  responded:            { icon: Mail,              color: "text-yellow-400", bg: "bg-yellow-500/10", label: "Responded" },
  booked:               { icon: CheckCircle2,      color: "text-green-400",  bg: "bg-green-500/10",  label: "Call Booked" },
};

// ─── Score badge ───────────────────────────────────────────────────────────

const ScoreBadge = ({ score }: { score: number }) => {
  const color =
    score >= 80 ? 'text-green-400 border-green-500/40' :
    score >= 65 ? 'text-[#FD802E] border-[#FD802E]/40' :
                  'text-gray-400 border-gray-500/40'
  return (
    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full border-2 font-bold text-sm shrink-0 ${color}`}>
      {score}
    </span>
  )
}

// ─── Queue row status badge ────────────────────────────────────────────────

const QueueStatusBadge = ({ status }: { status: QueueRowStatus }) => {
  if (status === 'generating') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FD802E]/10 text-[#FD802E] border border-[#FD802E]/30">
        <WaveIcon size="sm" animate className="shrink-0" /> Generating...
      </span>
    )
  }
  if (status === 'sending') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
        <Loader2 size={11} className="animate-spin" /> Sending...
      </span>
    )
  }
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
        <CheckCircle size={11} /> Sent
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
        Failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">
      Ready
    </span>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────

export function OutreachTracker() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = id ?? "";

  // Sent section state
  const [outreach, setOutreach] = useState<OutreachStateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [outreachError, setOutreachError] = useState(false);
  const [showExport, setShowExport] = useState(false);

  // Queue section state
  const [queueCandidates, setQueueCandidates] = useState<QueueCandidate[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [sendAllProgress, setSendAllProgress] = useState<string | null>(null);
  const [allSentDone, setAllSentDone] = useState(false);

  // Outreach brief (Message 1 template)
  const [outreachBrief, setOutreachBrief] = useState<string | null>(null);

  // Follow-up state
  const [followUpStatus, setFollowUpStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [followUpResult, setFollowUpResult] = useState<{ sent: number; day3: number; day7: number } | null>(null);

  // ── Load outreach (sent + stats) ──────────────────────────────────────────
  const loadOutreach = useCallback(() => {
    if (!projectId) return;
    setIsLoading(true);
    setOutreachError(false);
    engineApiClient.listOutreach(projectId)
      .then((res) => setOutreach(res.outreach))
      .catch(() => { setOutreach([]); setOutreachError(true); })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  // ── Load queue (pipeline_stage=outreach, not yet sent) ────────────────────
  const loadQueue = useCallback(() => {
    if (!projectId) return;
    setQueueLoading(true);

    Promise.all([
      engineApiClient.listCandidates(projectId),
      engineApiClient.listOutreach(projectId).catch(() => ({ projectId, outreach: [] })),
    ]).then(([candidateRes, outreachRes]) => {
      const outreachStatusMap = new Map<string, OutreachStatus>(
        outreachRes.outreach.map((o: { candidateId: string; status: OutreachStatus }) => [o.candidateId, o.status])
      );

      type OutreachEntry = {
        candidateId: string
        status: OutreachStatus
        draft?: { emailSubject?: string; emailBody?: string }
      }
      const draftMap = new Map<string, { subject: string; body: string }>(
        outreachRes.outreach.map((o: OutreachEntry) => [
          o.candidateId,
          {
            subject: o.draft?.emailSubject ?? '',
            body:    o.draft?.emailBody    ?? '',
          },
        ])
      );

      // Only candidates in 'outreach' pipeline stage whose outreach is not_started or prepared
      const pipelineCandidates = candidateRes.candidates.filter(
        (c) => (c.pipelineStage ?? '').toLowerCase() === 'outreach'
      );

      const rows: QueueCandidate[] = pipelineCandidates
        .filter((c) => {
          const status = outreachStatusMap.get(c.candidateId);
          // If no outreach record, or status is not_started/prepared → queue
          // If already sent/opened/etc → skip (will appear in sent section)
          if (!status || status === 'not_started' || status === 'prepared') return true;
          return false;
        })
        .map((c) => {
          const rawTitle = c.candidateTitle ?? '';
          const commaIdx = rawTitle.indexOf(', ');
          const roleTitle = commaIdx > -1 ? rawTitle.slice(0, commaIdx) : rawTitle;
          const company = commaIdx > -1 ? rawTitle.slice(commaIdx + 2) : '';
          const draft = draftMap.get(c.candidateId);

          const hasDraft = draft?.subject || draft?.body;
          const rowStatus: QueueRowStatus = hasDraft ? 'ready' : 'generating';

          return {
            id: c.candidateId,
            initials: c.candidateName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
            name: c.candidateName,
            title: roleTitle,
            company,
            match: c.fitScore,
            scoreBand: c.scoreBand,
            emailSubject: draft?.subject ?? '',
            emailBody:    draft?.body    ?? '',
            rowStatus,
          };
        });

      setQueueCandidates(rows);

      // Trigger generation for any still generating
      rows.forEach((row) => {
        if (row.rowStatus === 'generating') {
          engineApiClient.generateOutreach(projectId, row.id)
            .then((res) => {
              setQueueCandidates((prev) =>
                prev.map((r) =>
                  r.id === row.id
                    ? { ...r, emailSubject: res.draft.emailSubject, emailBody: res.draft.emailBody, rowStatus: 'ready' }
                    : r
                )
              );
            })
            .catch(() => {
              setQueueCandidates((prev) =>
                prev.map((r) => r.id === row.id ? { ...r, rowStatus: 'failed' } : r)
              );
            });
        }
      });
    })
    .catch(() => {/* silently fail — queue just won't show */})
    .finally(() => setQueueLoading(false));
  }, [projectId]);

  // Load outreach brief
  useEffect(() => {
    if (!projectId) return;
    engineApiClient.getOutputs(projectId)
      .then((res) => setOutreachBrief(res.drafts?.outreach ?? null))
      .catch(() => setOutreachBrief(null));
  }, [projectId]);

  useEffect(() => {
    loadOutreach();
    loadQueue();
  }, [loadOutreach, loadQueue]);

  // ── Stats (from sent section data) ───────────────────────────────────────
  const stats = {
    total:     outreach.length,
    sent:      outreach.filter((m) => !['not_started', 'prepared'].includes(m.status)).length,
    clicked:   outreach.filter((m) => ['link_clicked', 'pre_screen_started', 'pre_screen_completed'].includes(m.status)).length,
    completed: outreach.filter((m) => m.status === 'pre_screen_completed').length,
  };

  const exportContent = outreach.map((m) =>
    `Candidate: ${m.candidateName}\nStatus: ${m.status}${m.sentAt ? `\nSent: ${m.sentAt}` : ""}`
  ).join('\n\n---\n\n');

  // ── Queue helpers ─────────────────────────────────────────────────────────
  const truncate = (text: string, maxLen: number) =>
    text.length > maxLen ? text.slice(0, maxLen) + '…' : text;

  const bodyPreview = (body: string) => {
    const lines = body.split('\n').filter((l) => l.trim().length > 0).slice(0, 2);
    return truncate(lines.join(' '), 120);
  };

  const sendOne = async (candidateId: string) => {
    setQueueCandidates((prev) =>
      prev.map((r) => r.id === candidateId ? { ...r, rowStatus: 'sending' } : r)
    );
    try {
      await apiRequest({
        path: engineEndpoints.outreachSend(projectId, candidateId),
        method: 'POST',
      });
      // Remove from queue — will appear in sent section after refresh
      setQueueCandidates((prev) => prev.filter((r) => r.id !== candidateId));
      loadOutreach();
    } catch {
      setQueueCandidates((prev) =>
        prev.map((r) => r.id === candidateId ? { ...r, rowStatus: 'failed' } : r)
      );
    }
  };

  const sendAll = async () => {
    const readyCandidates = queueCandidates.filter((r) => r.rowStatus === 'ready');
    if (readyCandidates.length === 0) return;

    setAllSentDone(false);
    let sentCount = 0;

    for (const candidate of readyCandidates) {
      setSendAllProgress(`Sending ${sentCount + 1} of ${readyCandidates.length}...`);
      setQueueCandidates((prev) =>
        prev.map((r) => r.id === candidate.id ? { ...r, rowStatus: 'sending' } : r)
      );
      try {
        await apiRequest({
          path: engineEndpoints.outreachSend(projectId, candidate.id),
          method: 'POST',
        });
        setQueueCandidates((prev) => prev.filter((r) => r.id !== candidate.id));
      } catch {
        setQueueCandidates((prev) =>
          prev.map((r) => r.id === candidate.id ? { ...r, rowStatus: 'failed' } : r)
        );
      }
      sentCount++;
    }

    setSendAllProgress(null);
    setAllSentDone(true);
    loadOutreach();
  };

  const readyCount = queueCandidates.filter((r) => r.rowStatus === 'ready').length;
  const isSendingAll = queueCandidates.some((r) => r.rowStatus === 'sending');
  const sendAllDisabled = readyCount === 0 || isSendingAll;

  // Visible queue: candidates not yet sent (queue is already filtered, but exclude any locally-sent ones)
  const visibleQueue = queueCandidates.filter((r) => r.rowStatus !== 'sent');
  const showQueueSection = !queueLoading && visibleQueue.length > 0;

  // Sent records: outreach that has progressed past prepared/not_started
  const sentRecords = outreach.filter((m) => !['not_started', 'prepared'].includes(m.status));

  return (
    <div className="min-h-screen bg-[#02182B]">
      <ExportModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        content={exportContent}
        title="Outreach Report"
      />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/project/${id}`} className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-white text-xl font-semibold">Outreach</h1>
              <p className="text-gray-400 text-sm">Manage and track all candidate outreach</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {sendAllProgress && (
              <span className="text-sm text-gray-400 flex items-center gap-1.5">
                <Loader2 size={13} className="animate-spin text-[#FD802E]" />
                {sendAllProgress}
              </span>
            )}
            {allSentDone && (
              <span className="text-sm text-green-400 flex items-center gap-1.5">
                <CheckCircle size={13} /> All invites sent ✓
              </span>
            )}
            {showQueueSection && (
              <button
                onClick={sendAll}
                disabled={sendAllDisabled}
                className="flex items-center gap-2 bg-[#FD802E] text-[#233D4C] font-bold px-5 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={15} /> Send All Invites
                {readyCount > 0 && <span className="ml-1 text-[#233D4C]/70">({readyCount})</span>}
              </button>
            )}
            {sentRecords.length > 0 && (
              <button
                onClick={async () => {
                  setFollowUpStatus('sending');
                  setFollowUpResult(null);
                  try {
                    const res = await apiRequest<{ ok: boolean; sent: number; day3: number; day7: number }>({
                      path: engineEndpoints.outreachFollowUp(projectId),
                      method: 'POST',
                    });
                    setFollowUpResult({ sent: res.sent, day3: res.day3, day7: res.day7 });
                    setFollowUpStatus('done');
                  } catch {
                    setFollowUpStatus('error');
                  }
                }}
                disabled={followUpStatus === 'sending'}
                className="flex items-center gap-2 px-4 py-2 bg-[#233D4C] border border-[#2a4a5c] text-white rounded-lg hover:bg-[#2a4a5c] hover:border-[#FD802E]/40 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                title="Send follow-up emails to candidates who haven't responded (3 days) or final nudge (7 days)"
              >
                {followUpStatus === 'sending' ? (
                  <><Loader2 size={15} className="animate-spin" /> Sending…</>
                ) : (
                  <><Mail size={15} /> Send Follow-ups</>
                )}
              </button>
            )}
            {followUpStatus === 'done' && followUpResult !== null && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <CheckCircle size={13} />
                {followUpResult.sent === 0
                  ? 'No eligible candidates'
                  : `${followUpResult.sent} follow-up${followUpResult.sent !== 1 ? 's' : ''} sent`
                }
              </span>
            )}
            {followUpStatus === 'error' && (
              <span className="text-xs text-red-400">Follow-up failed — try again</span>
            )}
            <button
              onClick={() => setShowExport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors text-sm"
            >
              <Download size={16} /> Export Report
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">

        {/* ── Stats row ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total",           value: stats.total,     icon: Mail,              color: "text-gray-400" },
            { label: "Emails Sent",     value: stats.sent,      icon: Send,              color: "text-blue-400" },
            { label: "Link Clicked",    value: stats.clicked,   icon: MousePointerClick, color: "text-yellow-400" },
            { label: "Pre-Screen Done", value: stats.completed, icon: CheckCircle2,      color: "text-green-400" },
          ].map((s) => (
            <div key={s.label} className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">{s.label}</span>
                <s.icon className={s.color} size={18} />
              </div>
              <div className={`text-3xl font-bold ${s.color === "text-gray-400" ? "text-white" : s.color}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* ── Outreach Message Sequence ─────────────────────────────────── */}
        <div className="mb-8">
          <h2 className="text-white font-semibold text-base mb-5">Outreach Messages</h2>

          <div className="flex flex-col items-stretch gap-0">

            {/* Message 1 — Initial Outreach */}
            {(() => {
              const parsed = outreachBrief ? parseOutreachBrief(outreachBrief) : null;
              return (
                <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl overflow-hidden">
                  {/* Card header */}
                  <div className="flex items-center justify-between px-5 py-3 border-b border-[#2a4a5c] bg-[#02182B]/40">
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-[#FD802E] text-[#02182B] text-xs font-bold flex items-center justify-center">1</span>
                      <span className="text-white font-semibold text-sm">Initial Outreach</span>
                    </div>
                    <span className="text-xs text-gray-400 bg-[#02182B]/60 border border-[#2a4a5c] px-2.5 py-1 rounded-full">
                      Sent immediately when you click Send All
                    </span>
                  </div>

                  {/* Email preview body */}
                  {parsed ? (
                    <div className="px-6 py-5">
                      {/* Subject */}
                      <div className="mb-4 pb-4 border-b border-[#2a4a5c]">
                        <span className="text-gray-500 text-xs uppercase tracking-widest font-semibold">Subject</span>
                        <p className="text-white text-sm font-semibold mt-1">{parsed.subject || '[Subject line generated per candidate]'}</p>
                      </div>
                      {/* Body */}
                      <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">{parsed.body}</pre>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <p className="text-gray-500 text-sm">No outreach brief yet. Generate outputs from the project page.</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Sequence connector */}
            <div className="flex flex-col items-center py-1">
              <div className="w-px h-4 bg-[#2a4a5c]"/>
              <div className="flex items-center gap-2 bg-[#0f2535] border border-[#2a4a5c] rounded-full px-4 py-1.5 my-1">
                <Clock size={12} className="text-[#FD802E]" />
                <span className="text-xs text-gray-400 font-medium">No reply after 3 days → auto follow-up</span>
              </div>
              <div className="w-px h-4 bg-[#2a4a5c]"/>
              <div className="w-2 h-2 border-b-2 border-r-2 border-[#2a4a5c] rotate-45 -mt-1.5 mb-1"/>
            </div>

            {/* Message 2 — Follow-up */}
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl overflow-hidden">
              {/* Card header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#2a4a5c] bg-[#02182B]/40">
                <div className="flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">2</span>
                  <span className="text-white font-semibold text-sm">Follow-up</span>
                  <span className="text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full">🤖 Automatic</span>
                </div>
                <span className="text-xs text-gray-400 bg-[#02182B]/60 border border-[#2a4a5c] px-2.5 py-1 rounded-full">
                  Auto-sent if no response within 3 days
                </span>
              </div>

              {/* Email preview body */}
              <div className="px-6 py-5">
                <div className="mb-4 pb-4 border-b border-[#2a4a5c]">
                  <span className="text-gray-500 text-xs uppercase tracking-widest font-semibold">Subject</span>
                  <p className="text-white text-sm font-semibold mt-1">Quick follow-up: [original subject]</p>
                </div>
                <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">{`Hi [First Name],

I wanted to follow up on the opportunity I reached out about earlier this week. I know you're busy, so I'll keep this brief.

If you have 10–15 minutes, I'd love for you to take a look and share a bit about yourself:

[pre-screen link will be inserted on send]

No pressure at all — if the timing isn't right, just let me know.

Best,
Erica
Searchline.`}</pre>
              </div>
            </div>

          </div>
        </div>

        {/* ── Ready to Send section ─────────────────────────────────────── */}
        {showQueueSection && (
          <div className="mb-10">
            {/* Section header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-white font-semibold text-base">Ready to Send</h2>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#FD802E]/20 text-[#FD802E] text-xs font-bold">
                  {visibleQueue.length}
                </span>
              </div>
            </div>

            {/* Queue list */}
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl overflow-hidden">
              <ul className="divide-y divide-[#2a4a5c]">
                {visibleQueue.map((c) => (
                  <li key={c.id} className="px-5 py-4 hover:bg-[#02182B]/40 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-[#FD802E] rounded-full flex items-center justify-center text-[#233D4C] font-bold text-sm shrink-0 mt-0.5">
                        {c.initials}
                      </div>

                      {/* Main content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-1">
                          <span className="text-white font-semibold text-sm">{c.name}</span>
                          <ScoreBadge score={c.match} />
                          <QueueStatusBadge status={c.rowStatus} />
                        </div>

                        {(c.title || c.company) && (
                          <p className="text-gray-400 text-xs mb-2">
                            {[c.title, c.company].filter(Boolean).join(' · ')}
                          </p>
                        )}

                        {c.rowStatus === 'generating' ? (
                          <div className="flex items-center gap-2 text-[#FD802E]/70 text-xs py-2">
                            <WaveIcon size="sm" animate className="shrink-0" />
                            Generating outreach message…
                          </div>
                        ) : (c.emailSubject || c.emailBody) ? (
                          <div className="bg-[#02182B]/60 border border-[#2a4a5c] rounded-lg px-3 py-2.5 mt-2">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Mail size={11} className="text-gray-500 shrink-0" />
                              <span className="text-gray-300 text-xs font-medium truncate">
                                {truncate(c.emailSubject, 80)}
                              </span>
                            </div>
                            {c.emailBody && (
                              <p className="text-gray-500 text-xs leading-relaxed">
                                {bodyPreview(c.emailBody)}
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 shrink-0 mt-0.5">
                        <button
                          onClick={() => navigate(`/project/${id}/candidate/${c.id}/outreach`)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a2e3d] border border-[#2a4a5c] text-gray-300 hover:text-white hover:border-[#3a5a6c] text-xs font-medium rounded-lg transition-colors"
                        >
                          <Edit3 size={11} /> Edit
                        </button>
                        <button
                          onClick={() => sendOne(c.id)}
                          disabled={c.rowStatus !== 'ready'}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FD802E] text-[#233D4C] font-semibold text-xs rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {c.rowStatus === 'sending' ? (
                            <><Loader2 size={11} className="animate-spin" /> Sending…</>
                          ) : (
                            <><Send size={11} /> Send</>
                          )}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* ── Sent section ──────────────────────────────────────────────── */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader size={18} className="animate-spin text-[#FD802E]" />
            <span>Loading outreach…</span>
          </div>
        ) : outreachError ? (
          <div className="flex flex-col items-center justify-center gap-4 bg-[#233D4C] border border-red-500/20 rounded-xl py-16">
            <AlertCircle size={28} className="text-red-400" />
            <p className="text-red-400 text-sm font-medium">Unable to load outreach data. Try refreshing.</p>
            <button
              onClick={loadOutreach}
              className="text-xs text-gray-300 hover:text-white border border-[#2a4a5c] px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : sentRecords.length === 0 && !showQueueSection ? (
          // Empty state — nothing sent and no queue
          <div className="bg-[#233D4C] border border-dashed border-[#2a4a5c] rounded-xl p-16 text-center">
            <div className="w-12 h-12 bg-[#FD802E]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <MessageSquare size={22} className="text-[#FD802E]" />
            </div>
            <p className="text-white font-semibold mb-1">No outreach yet</p>
            <p className="text-gray-400 text-sm">Go to a candidate profile to generate and send outreach.</p>
          </div>
        ) : sentRecords.length > 0 ? (
          <div>
            {/* Only show "Sent" header when queue section is also visible */}
            {showQueueSection && (
              <h2 className="text-white font-semibold text-base mb-4">Sent</h2>
            )}
            <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] overflow-hidden">
              <div className="bg-[#02182B] px-6 py-3 border-b border-[#2a4a5c]">
                <div className="grid grid-cols-12 gap-4 text-gray-400 text-xs font-medium uppercase">
                  <div className="col-span-4">Candidate</div>
                  <div className="col-span-5">Subject</div>
                  <div className="col-span-3">Status</div>
                </div>
              </div>
              <div className="divide-y divide-[#2a4a5c]">
                {sentRecords.map((record) => {
                  const cfg = STATUS_CONFIG[record.status] ?? STATUS_CONFIG['not_started'];
                  return (
                    <div
                      key={record.candidateId}
                      className="px-6 py-4 hover:bg-[#02182B]/50 transition-colors cursor-pointer group"
                      onClick={() => navigate(`/project/${id}/candidate/${record.candidateId}/outreach`)}
                    >
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-4">
                          <div className="text-white font-medium text-sm group-hover:text-[#FD802E] transition-colors">
                            {record.candidateName}
                          </div>
                          {record.sentAt && (
                            <div className="text-gray-500 text-xs mt-0.5">
                              Sent {new Date(record.sentAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <div className="col-span-5">
                          <div className="text-gray-300 text-sm truncate">
                            {record.draft?.emailSubject ?? '—'}
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className={`flex items-center gap-1.5 ${cfg.bg} ${cfg.color} px-2.5 py-1 rounded-full text-xs font-medium w-fit`}>
                            <cfg.icon size={12} />
                            {cfg.label}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

      </main>
    </div>
  );
}
